import { NetworkErrorService } from '@app/services/NetworkError.service';
import { ErrorHandlerService } from '@app/services/ErrorHandler.service';
import { PropertyGroupApi } from '@app/services/apiServices/PropertyGroupApi';
import { SpinnerService } from '@app/services/spinner.service';
import { Property } from "@app/models/Property";
import { PropertyGroupCandidate } from "@app/models/PropertyGroupCandidate";
import { PropertyGroup } from "@app/models/PropertyGroup";
import { PropertyApi } from "@app/services/apiServices/PropertyApi";
import { UserStore } from "@app/services/stores/UserStore";
import { take } from "rxjs/operators";
import { Component, Inject, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { DialogErrorDialog } from "@app/components/sharedDialogs/dialogError.dialog";
import { OneButtonDialogComponent } from "@app/components/sharedDialogs/oneButtonDialog.component";
import { ActivatedRoute, Router } from "@angular/router";

@Component({
    selector: 'app-property-multi-relationships',
    template: require('./propertyMultiRelationships.html')
})
export class PropertyMultiRelationshipsComponent implements OnInit {

    propertyId: number;
    property: Property;
    groupCandidates: PropertyGroupCandidate[];
    availableGroups: PropertyGroup[];
    sortedAvailableGroups: PropertyGroup[];
    propertyGroupNicknameError: boolean = false;
    doneloading: boolean = false;
    nickname: string;
    staticNickname: string = "";
    staticPmcCouponId: string = "";
    staticPmcName: string= "";
    $stateParams: any;

    constructor(@Inject(MatDialog) private dialog: MatDialog,
                @Inject(PropertyApi) private propertyApi: PropertyApi,
                @Inject(PropertyGroupApi) private propertyGroupApi: PropertyGroupApi,
                @Inject(NetworkErrorService) private networkErrorService: NetworkErrorService,
                @Inject(ErrorHandlerService) private errorHandlerService: ErrorHandlerService,
                @Inject(SpinnerService) private spinnerService: SpinnerService,
                @Inject(UserStore) private userStore: UserStore,
                @Inject(Router) private router: Router,
                @Inject(ActivatedRoute) private route: ActivatedRoute) {

        if(this.router.getCurrentNavigation()) {
            this.$stateParams = this.router.getCurrentNavigation().extras.state;
        }

    }

    ngOnInit() {
        if (this.route.snapshot.params.propertyId) {
            this.spinnerService.showSpinner();
            this.propertyId = this.route.snapshot.params.propertyId;
            this.nickname = this.$stateParams.nickname;
            this.loadData().then(() => {
                this.spinnerService.closeSpinner();
                this.doneloading = true;
            }, () => {
                this.spinnerService.closeSpinner();
                this.cancel();
            });
        }
    }

    loadData() {
        return Promise.all([
            this.loadProperty(),
            this.loadAvailableGroups(),
            this.loadPropertyGroupCandidates()
        ]);
    }

    loadProperty(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.propertyApi.propertiesIdGet(this.propertyId)
                .pipe(take(1)).subscribe((resp) => {
                    this.property = resp;
                    this.nickname = this.property.address.line1;
                    this.staticNickname = this.nickname ?? "";
                    this.staticPmcCouponId = this.property.hoa.pmc.couponId ?? "";
                    this.staticPmcName= this.property.hoa.pmc.name ?? "";
                    resolve();
                }, (err) => {
                    this.networkErrorService.handleNetworkError(err, true)
                        .pipe(take(1)).subscribe((resp) => {
                            if (resp === true) {
                                this.loadProperty();
                            }
                            reject();
                        });
                });
        });
    }

    loadPropertyGroupCandidates(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            let userId = this.userStore.getHoaHomeownerId();
            this.propertyGroupApi.getPropertyGroupCandidates(userId, this.propertyId)
                .pipe(take(1)).subscribe((candidateResp) => {
                    this.groupCandidates = candidateResp;
                    resolve();
                }, (err) => {
                    this.networkErrorService.handleNetworkError(err, true)
                        .pipe(take(1)).subscribe((resp) => {
                            if (resp === true) {
                                this.loadPropertyGroupCandidates();
                            }
                            reject();
                        });
                });
        });
    }

    loadAvailableGroups(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            let userId = this.userStore.getHoaHomeownerId();
            this.propertyGroupApi.getAvailableGroups(userId, this.propertyId)
                .pipe(take(1)).subscribe((resp) => {
                    this.availableGroups = resp;
                    this.sortAvailableGroups();
                    resolve();
                }, (err) => {
                    this.networkErrorService.handleNetworkError(err, true)
                        .pipe(take(1)).subscribe((resp) => {
                            if (resp === true) {
                                this.loadAvailableGroups();
                            }
                            reject();
                        });
                });
        });
    }

    nicknameChanged() {
        if (this.nickname === undefined) {
            this.propertyGroupNicknameError = true;
        } else {
            this.propertyGroupNicknameError = !this.nickname || this.nickname.length === 0;
        }
    }


    invalid() {
        return !this.nickname || this.nickname.length === 0;
    }

    createPropertyGroup() {
        this.router.navigateByUrl('/properties/propertyGroup/create/' + this.property.id,
            { state: { propertyId: this.property.id, nickname: this.nickname } });
    }

    cancel() {
        this.router.navigateByUrl('/properties');
    }

    addToPropertyGroup(propertyGroupId: number) {
        this.spinnerService.showSpinner();
        const propertyIds: number[] = [this.propertyId];
        this.propertyGroupApi.propertyGroupAddProperties(propertyGroupId, propertyIds)
            .pipe(take(1)).subscribe(() => {
                this.spinnerService.closeSpinner();
                this.router.navigateByUrl('/properties/propertyGroup/' + propertyGroupId,
                    { state: { propertyGroupId: propertyGroupId } });
            }, (err) =>{
                const dialogRef = this.dialog.open(DialogErrorDialog);
                dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
                    this.spinnerService.closeSpinner();
                    this.networkErrorService.handleNetworkError(err, true)
                        .pipe(take(1)).subscribe((resp) => {
                            if (resp === true) {
                                this.addToPropertyGroup(propertyGroupId);
                            } else {
                                this.router.navigateByUrl('/properties');
                            }
                        });
                })
            });
    }

    createGroupHelper() {
        this.dialog.open(OneButtonDialogComponent, {
            data: {
                "header": "Create Group",
                "message": "Create a new group to associate accounts with the same address that are not already in a group",
                "buttonText": "Close"
            }
        });

    }

    addToGroupHelper() {
        this.dialog.open(OneButtonDialogComponent, {
            data: {
                "header": "Add to Group",
                "message": "Add account to an existing group with the same address",
                "buttonText": "Close"
            }
        });
    }

    groupNameHelper() {
        this.dialog.open(OneButtonDialogComponent, {
            data: {
                "header": "Group Nickname",
                "message": "Provide a name for the related accounts group.  This will be the name displayed on the home page that can be expanded to show the accounts in the group.",
                "buttonText": "Close"
            }
        });
    }

    private sortAvailableGroups() {
        this.sortedAvailableGroups = [];

        if (!this.availableGroups || this.availableGroups.length === 0) {
            return;
        } else if(this.availableGroups.length === 1) {
            this.sortedAvailableGroups.push(this.availableGroups[0]);
        }

        this.sortedAvailableGroups = this.availableGroups.sort((a, b) => {

            return b.id - a.id;

        })

    }
}
